
import React from 'react';
import { Mail, Zap, Radio, Send, ArrowDownLeft } from 'lucide-react';
import { PropertyField } from '../../molecules/PropertyField';
import { WorkflowNodeData } from '../../../types';

interface MessageSignalTabProps {
    data: WorkflowNodeData;
    onChange: (key: string, value: any) => void;
    type: 'message' | 'signal';
    isThrowing?: boolean; // True for Throw/End events
}

export const MessageSignalTab: React.FC<MessageSignalTabProps> = ({ data, onChange, type, isThrowing }) => {
    const isMessage = type === 'message';
    const Icon = isMessage ? Mail : (isThrowing ? Radio : Zap);
    const colorClass = isMessage ? 'text-blue-600 dark:text-blue-400' : 'text-orange-600 dark:text-orange-400';
    const bgClass = isMessage ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-900' : 'bg-orange-50 dark:bg-orange-900/20 border-orange-100 dark:border-orange-900';
    
    return (
        <div className="p-6 space-y-6">
            <div className={`p-3 rounded-lg border flex gap-3 ${bgClass}`}>
                <Icon className={`${colorClass} mt-0.5 shrink-0`} size={16} />
                <div className="space-y-1">
                    <p className={`text-xs font-bold ${isMessage ? 'text-blue-800 dark:text-blue-300' : 'text-orange-800 dark:text-orange-300'}`}>
                        {isMessage ? 'Message' : 'Signal'} Configuration
                    </p>
                    <p className={`text-[11px] ${isMessage ? 'text-blue-700 dark:text-blue-400' : 'text-orange-700 dark:text-orange-400'} leading-relaxed`}>
                        {isThrowing 
                            ? `Configure the ${type} to broadcast/send. Downstream processes waiting for this ${type} name will be triggered.` 
                            : `Configure the ${type} to wait for. This node will activate when a ${type} with this name is received.`}
                    </p>
                </div>
            </div>

            <div className="space-y-4">
                <PropertyField 
                    config={{ 
                        name: isMessage ? 'messageName' : 'signalName', 
                        label: `${isMessage ? 'Message' : 'Signal'} Name`, 
                        type: 'text', 
                        placeholder: isMessage ? 'OrderPlaced' : 'GlobalAlert',
                        validation: { required: true },
                        description: 'Must match exactly with the corresponding throw/catch event.'
                    }}
                    value={isMessage ? data.messageName : data.signalName}
                    onChange={onChange}
                />

                {!isThrowing && isMessage && (
                     <PropertyField
                        config={{
                            name: 'correlationKey',
                            label: 'Correlation Key',
                            type: 'text',
                            placeholder: '${orderId}',
                            description: 'Used to match the message to a specific process instance.'
                        }}
                        value={data.correlationKey}
                        onChange={onChange}
                    />
                )}

                {!isThrowing && isMessage && (
                    <PropertyField
                        config={{
                            name: 'correlationScript',
                            label: 'Correlation Script',
                            type: 'expression',
                            rows: 4,
                            placeholder: "message.EntityType === 'Person' && message.Action === 'Upsert'",
                            description: 'JavaScript expression evaluated against the incoming message object. Must return a boolean. For intermediate/boundary events, process variables are also available.'
                        }}
                        value={data.correlationScript}
                        onChange={onChange}
                    />
                )}

                <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                        {isThrowing ? <Send size={12}/> : <ArrowDownLeft size={12}/>} Payload Data
                    </label>
                    <div className="bg-slate-50 dark:bg-slate-900 p-3 rounded-lg border border-slate-200 dark:border-slate-800">
                        <PropertyField 
                            config={{ 
                                name: isMessage ? 'messagePayload' : 'signalPayload', 
                                label: '', // hidden
                                type: 'object', 
                                rows: 6,
                                placeholder: '{\n  "key": "value"\n}',
                                description: isThrowing ? 'Data to send with the event' : 'Schema expected from the event'
                            }}
                            value={isMessage ? data.messagePayload : data.signalPayload}
                            onChange={onChange}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
